import { CreditCard, Landmark, MoreVertical } from "lucide-react";
import * as React from "react";
import { Badge, type BadgeVariant } from "../atoms/badge.js";
import { InlineIconButton } from "../atoms/inline-icon-button.js";
import { cx } from "../internal/cx.js";

export type PaymentMethodCardType = "bank" | "credit-card";

export type PaymentMethodCardStatusTone = "success" | "warning" | "danger" | "neutral";

export interface PaymentMethodCardStatusBadge {
  /** Stable id. */
  id: string;
  /** Display label ("Active", "Pending verification"). */
  label: React.ReactNode;
  /** Color tone. */
  tone?: PaymentMethodCardStatusTone;
}

export interface PaymentMethodCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Payment method type. Drives the leading icon. */
  type?: PaymentMethodCardType;
  /** Account name ("Operating checking", "Visa"). */
  name: React.ReactNode;
  /** Already-formatted account/card number ("Bank account ending in 4242"). If a string ending in four digits, that suffix is shown semibold. */
  number: React.ReactNode;
  /** Status badges rendered below the title with the Primary chip (when shown). */
  statusBadges?: PaymentMethodCardStatusBadge[];
  /** Show the "Primary" chip next to the name. Default true. */
  showPrimaryChip?: boolean;
  /** Custom label for the Primary chip. */
  primaryChipLabel?: React.ReactNode;
  /** Render the menu trigger button. */
  onMenuClick?: () => void;
  /** Accessible label for the menu trigger. Default "Open payment method menu". */
  menuAriaLabel?: string;
  /** Custom trailing/menu slot. Use for dropdown menu triggers in manage-many surfaces. */
  menu?: React.ReactNode;
  /** Optional microdeposit step control (e.g. `Button` with `variant="outline"` `size="sm"`). */
  verificationLink?: React.ReactNode;
  /** Optional contextual copy below the masked number. */
  supportingText?: React.ReactNode;
  /** Tone for supporting copy. Default "muted". */
  supportingTextTone?: "muted" | "danger";
}

function toneToBadgeVariant(tone: PaymentMethodCardStatusTone | undefined): BadgeVariant {
  if (tone === "success") return "success";
  if (tone === "warning") return "warning";
  if (tone === "danger") return "danger";
  return "neutral";
}

/** When `number` is a plain string ending in four digits, wraps the suffix for semibold styling. */
function renderPaymentMethodNumberLine(number: React.ReactNode): React.ReactNode {
  if (typeof number !== "string") return number;
  const match = number.match(/^(.*?)(\d{4})$/s);
  if (!match) return number;
  const [, prefix, last4] = match;
  return (
    <>
      {prefix}
      <span className="cw-v2-payment-method-card__number-last4">{last4}</span>
    </>
  );
}

/**
 * Single primary payment-method tile. Used inline on a dashboard's
 * billing-overview section: account icon + name + grouped Primary/status
 * badges below the title, then the (last-4-formatted) number line, an
 * optional microdeposit-verification slot, supporting copy, and an
 * optional menu trigger.
 */
export const PaymentMethodCard = React.forwardRef<HTMLDivElement, PaymentMethodCardProps>(
  function PaymentMethodCard(
    {
      className,
      menu,
      menuAriaLabel = "Open payment method menu",
      name,
      number,
      onMenuClick,
      primaryChipLabel = "Primary",
      showPrimaryChip = true,
      statusBadges,
      supportingText,
      supportingTextTone = "muted",
      type = "bank",
      verificationLink,
      ...props
    },
    ref,
  ) {
    const TypeIcon = type === "credit-card" ? CreditCard : Landmark;
    return (
      <div ref={ref} className={cx("cw-v2-payment-method-card", className)} {...props}>
        <div aria-hidden className="cw-v2-payment-method-card__icon">
          <TypeIcon size={20} />
        </div>
        <div className="cw-v2-payment-method-card__body">
          <div className="cw-v2-payment-method-card__title-row">
            <span className="cw-v2-payment-method-card__name">{name}</span>
          </div>
          {showPrimaryChip || (statusBadges?.length ?? 0) > 0 ? (
            <div className="cw-v2-payment-method-card__badges">
              {showPrimaryChip ? (
                <Badge size="sm" variant="brand">
                  {primaryChipLabel}
                </Badge>
              ) : null}
              {statusBadges?.map((badge) => (
                <Badge key={badge.id} size="sm" variant={toneToBadgeVariant(badge.tone)}>
                  {badge.label}
                </Badge>
              ))}
            </div>
          ) : null}
          <div className="cw-v2-payment-method-card__number">
            {renderPaymentMethodNumberLine(number)}
          </div>
          {supportingText ? (
            <div
              className={cx(
                "cw-v2-payment-method-card__supporting-text",
                `cw-v2-payment-method-card__supporting-text--${supportingTextTone}`,
              )}
            >
              {supportingText}
            </div>
          ) : null}
          {verificationLink ? (
            <div className="cw-v2-payment-method-card__verify">{verificationLink}</div>
          ) : null}
        </div>
        {menu || onMenuClick ? (
          <div className="cw-v2-payment-method-card__trailing">
            {menu ?? (
              <InlineIconButton
                aria-label={menuAriaLabel}
                className="cw-v2-payment-method-card__menu"
                icon={<MoreVertical aria-hidden />}
                onClick={onMenuClick}
                size="md"
              />
            )}
          </div>
        ) : null}
      </div>
    );
  },
);
PaymentMethodCard.displayName = "PaymentMethodCard";

export interface PaymentMethodCardEmptyProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Empty-state title. Default "No payment method yet". */
  title?: React.ReactNode;
  /** Body copy. */
  description?: React.ReactNode;
  /** Primary CTA (e.g. Button). Placed below the description in the text column (aligned with copy, not the icon). */
  action?: React.ReactNode;
  /** Optional supporting icon. */
  icon?: React.ReactNode;
}

/** Empty-state companion to `<PaymentMethodCard>`. Renders the same chrome
 * with a placeholder copy + CTA when no payment method has been added yet. */
export const PaymentMethodCardEmpty = React.forwardRef<HTMLDivElement, PaymentMethodCardEmptyProps>(
  function PaymentMethodCardEmpty(
    { action, className, description, icon, title = "No payment method yet", ...props },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cx("cw-v2-payment-method-card", "cw-v2-payment-method-card--empty", className)}
        {...props}
      >
        <div aria-hidden className="cw-v2-payment-method-card__icon">
          {icon ?? <CreditCard size={20} />}
        </div>
        <div className="cw-v2-payment-method-card__body">
          <div className="cw-v2-payment-method-card__name">{title}</div>
          {description ? (
            <div className="cw-v2-payment-method-card__number">{description}</div>
          ) : null}
          {action ? <div className="cw-v2-payment-method-card__action">{action}</div> : null}
        </div>
      </div>
    );
  },
);
PaymentMethodCardEmpty.displayName = "PaymentMethodCardEmpty";
